import { useState } from "react";
import { Lock, MessageCircle, Plus, ShieldCheck, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Empty, PageHeader, StatusBadge } from "@/components/shared";
import { AuditList } from "@/components/AuditList";
import { api, errMsg, fdate, fdt, label, useApi, waPhone } from "@/lib/api";

const STATUSES = ["OPEN", "IN_PROGRESS", "WAITING_ON_CLIENT", "RESOLVED", "CLOSED"];
const PRIORITIES = ["LOW", "MEDIUM", "HIGH", "URGENT"];

export function AmcBadge({ active, expiry }) {
  return active ? (
    <div className="flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm font-medium text-emerald-700 dark:text-emerald-400" data-testid="amc-badge-active">
      <ShieldCheck className="h-4 w-4" />Privilege Package (AMC) Active — Expires on {fdate(expiry)}
    </div>
  ) : (
    <div className="flex items-center gap-2 rounded-md border bg-muted/60 px-3 py-2 text-sm font-medium text-muted-foreground" data-testid="amc-badge-none">
      <ShieldOff className="h-4 w-4" />No Active AMC
    </div>
  );
}

const waLink = (t, c) => {
  const text = `*Gamerz Nation Support — ${t.ticketNumber}*\n\nHi ${t.customerName},\n\n${c.content}\n\n— Team Gamerz Nation\nsupport@gamerznation.com | +91 80 4567 8900`;
  return `https://api.whatsapp.com/send?phone=${waPhone(t.customerPhone)}&text=${encodeURIComponent(text)}`;
};

import { SlaBadge, TicketCustomerPicker } from "@/components/TicketCustomer";
function NewTicketDialog({ open, onClose, onCreated }) {
  const { data: customers } = useApi("/customers");
  const { data: projects } = useApi("/projects");
  const empty = { customerId: "", projectId: "", customerName: "", customerPhone: "", customerEmail: "", subject: "", description: "", priority: "MEDIUM" };
  const [f, setF] = useState(empty);
  const cust = customers?.find((c) => c.id === f.customerId);
  const amcActive = cust?.hasActiveAMC && cust.amcExpiryDate && new Date(cust.amcExpiryDate) > new Date();
  const pickCustomer = (cid) => {
    const c = customers.find((x) => x.id === cid);
    setF({ ...f, customerId: cid, customerName: c.name, customerPhone: c.phone, customerEmail: c.email || "" });
  };
  const save = async () => {
    try {
      const r = await api.post("/tickets", { ...f, customerId: f.customerId || null, projectId: f.projectId || null });
      toast.success(`Ticket ${r.data.ticketNumber} created`);
      setF(empty);
      onCreated(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl" data-testid="new-ticket-dialog">
        <DialogHeader><DialogTitle>New support ticket</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 space-y-1.5"><Label>Customer</Label>
            <TicketCustomerPicker customers={customers} projects={projects}
              onPick={(r) => setF({ ...f, customerId: r.customerId || "", projectId: r.projectId || f.projectId, customerName: r.name, customerPhone: r.phone, customerEmail: r.email || "" })}
              onNew={() => setF({ ...f, customerId: "", projectId: "", customerName: "", customerPhone: "", customerEmail: "" })} />
            <p className="text-[11px] text-muted-foreground">New customers are saved automatically. Resolution due in 5 days (SLA).</p></div>
          {f.customerId && <div className="col-span-2"><AmcBadge active={amcActive} expiry={cust?.amcExpiryDate} /></div>}
          <div className="space-y-1.5"><Label>Name *</Label><Input value={f.customerName} onChange={set("customerName")} data-testid="ticket-name-input" /></div>
          <div className="space-y-1.5"><Label>Phone *</Label><Input value={f.customerPhone} onChange={set("customerPhone")} data-testid="ticket-phone-input" /></div>
          <div className="space-y-1.5"><Label>Linked project</Label>
            <Select value={f.projectId} onValueChange={(v) => setF({ ...f, projectId: v })}>
              <SelectTrigger data-testid="ticket-project-select"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>{projects?.map((p) => <SelectItem key={p.id} value={p.id}>{p.projectCode} · {p.customerName}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>Priority</Label>
            <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}>
              <SelectTrigger data-testid="ticket-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{label(p)}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="col-span-2 space-y-1.5"><Label>Subject *</Label><Input value={f.subject} onChange={set("subject")} data-testid="ticket-subject-input" /></div>
          <div className="col-span-2 space-y-1.5"><Label>Description *</Label><Textarea rows={3} value={f.description} onChange={set("description")} data-testid="ticket-description-input" /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={!f.customerName || !f.customerPhone || !f.subject || !f.description} data-testid="create-ticket-button">Create ticket</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TicketDetail({ t, onUpdate }) {
  const [content, setContent] = useState("");
  const [internal, setInternal] = useState(false);
  const [auditKey, setAuditKey] = useState(0);
  const amcActive = t.hasActiveAMC && t.amcExpiryDate && new Date(t.amcExpiryDate) > new Date();
  const patch = async (body) => {
    onUpdate({ ...t, ...body });
    try { onUpdate((await api.patch(`/tickets/${t.id}`, body)).data); setAuditKey((k) => k + 1); } catch (e) { onUpdate(t); toast.error(errMsg(e)); }
  };
  const post = async () => {
    try { onUpdate((await api.post(`/tickets/${t.id}/comments`, { content, isInternal: internal })).data); setContent(""); setAuditKey((k) => k + 1); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-lg border bg-card" data-testid="ticket-detail">
      <div className="p-5 border-b space-y-3">
        <div className="flex flex-wrap items-center gap-2"><span className="font-mono text-xs text-muted-foreground">{t.ticketNumber}</span><StatusBadge status={t.priority} /><SlaBadge t={t} />{t.dueDate && <span className="text-[11px] text-muted-foreground">Due {fdate(t.dueDate)}</span>}</div>
        <h2 className="text-lg font-semibold" data-testid="ticket-subject">{t.subject}</h2>
        <div className="text-sm text-muted-foreground">{t.customerName} · {t.customerPhone} {t.customerEmail && `· ${t.customerEmail}`}</div>
        <AmcBadge active={amcActive} expiry={t.amcExpiryDate} />
        <div className="flex flex-wrap gap-2">
          <Select value={t.status} onValueChange={(v) => patch({ status: v })}>
            <SelectTrigger className="w-48" data-testid="ticket-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{label(s)}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={t.priority} onValueChange={(v) => patch({ priority: v })}>
            <SelectTrigger className="w-32" data-testid="ticket-priority-update"><SelectValue /></SelectTrigger>
            <SelectContent>{PRIORITIES.map((s) => <SelectItem key={s} value={s}>{label(s)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <Tabs defaultValue="thread" className="p-5">
        <TabsList><TabsTrigger value="thread" data-testid="ticket-tab-thread">Conversation</TabsTrigger><TabsTrigger value="audit" data-testid="ticket-tab-audit">Audit Log</TabsTrigger></TabsList>
        <TabsContent value="thread" className="space-y-3 mt-4">
          <div className="rounded-md bg-muted/50 p-3 text-sm">{t.description}</div>
          {t.comments.map((c) => (
            <div key={c.id} className={`rounded-md border p-3 text-sm ${c.isInternal ? "bg-yellow-50 border-yellow-200 dark:bg-yellow-500/10 dark:border-yellow-500/30" : "bg-card"}`} data-testid={`comment-${c.id}`}>
              <div className="flex items-center gap-2 text-xs mb-1.5">
                <span className="font-medium">{c.authorName}</span>
                {c.isInternal ? <span className="flex items-center gap-1 text-yellow-700 dark:text-yellow-400"><Lock className="h-3 w-3" />Internal note</span> : <span className="text-muted-foreground">Customer message</span>}
                <span className="ml-auto text-muted-foreground">{fdt(c.createdAt)}</span>
              </div>
              <div className="whitespace-pre-wrap">{c.content}</div>
              {c.shareableOnWhatsApp && (
                <a href={waLink(t, c)} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 hover:underline" data-testid="whatsapp-share-button">
                  <MessageCircle className="h-3.5 w-3.5" />Share on WhatsApp
                </a>
              )}
            </div>
          ))}
          <div className={`rounded-md border p-3 space-y-3 ${internal ? "bg-yellow-50/60 dark:bg-yellow-500/5" : ""}`}>
            <Textarea rows={3} value={content} onChange={(e) => setContent(e.target.value)} placeholder={internal ? "Internal note (team only)" : "Message to customer"} className="bg-background" data-testid="comment-input" />
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-xs"><Switch checked={internal} onCheckedChange={setInternal} data-testid="comment-internal-toggle" />Internal team note</label>
              <Button size="sm" onClick={post} disabled={!content.trim()} data-testid="post-comment-button">Post</Button>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="audit" className="mt-4"><AuditList entityType="TICKET" entityId={t.id} refreshKey={auditKey} /></TabsContent>
      </Tabs>
    </div>
  );
}

export default function Tickets() {
  const { data, setData } = useApi("/tickets");
  const [selId, setSelId] = useState(null);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("ALL");
  const list = (data || []).filter((t) => filter === "ALL" || t.status === filter);
  const sel = (data || []).find((t) => t.id === (selId || list[0]?.id));
  const onUpdate = (t) => setData((list) => list.map((x) => (x.id === t.id ? t : x)));
  return (
    <div>
      <PageHeader title="Support Tickets" subtitle="After-sales support with AMC visibility and WhatsApp updates.">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44" data-testid="ticket-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="ALL">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{label(s)}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={() => setOpen(true)} data-testid="new-ticket-button"><Plus className="h-4 w-4 mr-2" />New ticket</Button>
      </PageHeader>
      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 rounded-lg border bg-card divide-y self-start" data-testid="ticket-inbox">
          {list.length === 0 && <Empty text="No tickets" />}
          {list.map((t) => (
            <button key={t.id} onClick={() => { setSelId(t.id); if (window.innerWidth < 1024) setTimeout(() => document.querySelector('[data-testid="ticket-detail"]')?.scrollIntoView({ behavior: "smooth" }), 50); }} data-testid={`ticket-item-${t.ticketNumber}`}
              className={`w-full text-left px-4 py-3.5 transition-colors ${sel?.id === t.id ? "bg-secondary/60" : "hover:bg-secondary/30"}`}>
              <div className="flex items-center gap-2 text-xs"><span className="font-mono text-muted-foreground">{t.ticketNumber}</span><StatusBadge status={t.status} /><SlaBadge t={t} />
                {t.hasActiveAMC && <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />}<span className="ml-auto text-muted-foreground">{fdate(t.updatedAt)}</span></div>
              <div className="text-sm font-medium mt-1.5 truncate">{t.subject}</div>
              <div className="text-xs text-muted-foreground">{t.customerName}</div>
            </button>
          ))}
        </div>
        <div className="lg:col-span-3">{sel ? <TicketDetail key={sel.id} t={sel} onUpdate={onUpdate} /> : <Empty text="Select a ticket" />}</div>
      </div>
      <NewTicketDialog open={open} onClose={() => setOpen(false)} onCreated={(t) => { setData([t, ...(data || [])]); setSelId(t.id); setOpen(false); }} />
    </div>
  );
}
